import { useState } from 'react';
import { usePracticeTextFunnel } from '../../hooks';
import type { FunnelRange } from '../../api/commandCenterApi';
import { StepDetail, TextFunnel } from '@/modules/reviews/analytics/TextFunnel';
import {
  AnalyticsCard,
  ByDelayCard,
  ByProviderCard,
  CostCard,
  Delta,
  DeliveryCard,
  Kpi,
  RangeSwitch,
  RatingMixCard,
  RecentConversationsCard,
  ReplyByHourCard,
  StaffCard,
} from '@/modules/reviews/analytics/AnalyticsCards';
import { formatMinutes, rateText, usd } from '@/modules/reviews/analytics/format';
import type { TextStepKey } from '@/modules/reviews/analytics/types';
import a from '@/modules/reviews/analytics/analytics.module.css';

/**
 * The text funnel for one practice (PracticeNova-internal): every confirmed visit from the front
 * desk to a review on Google, where patients stopped and why, delivery, staff hand-offs, cost and
 * the latest conversations.
 */
export function TextFunnelPanel({
  practiceId,
  locationId,
  range,
  onRangeChange,
}: {
  practiceId: string;
  locationId?: string;
  range: FunnelRange;
  onRangeChange: (r: FunnelRange) => void;
}) {
  const {
    data: view,
    isLoading,
    isError,
  } = usePracticeTextFunnel(practiceId, {
    range,
    ...(locationId ? { locationId } : {}),
  });
  const [selected, setSelected] = useState<TextStepKey | null>(null);

  const toolbar = (
    <div className={a.toolbar}>
      <div>
        <h3 className={a.cardTitle}>Text funnel</h3>
        <p className={a.cardSub}>
          Visits confirmed in the last {range}, followed to the end. Click a step for detail.
        </p>
      </div>
      <RangeSwitch value={range} onChange={onRangeChange} />
    </div>
  );

  if (isLoading)
    return (
      <div className={a.stack}>
        {toolbar}
        <p className={a.muted}>Loading…</p>
      </div>
    );
  if (isError || !view)
    return (
      <div className={a.stack}>
        {toolbar}
        <p className={a.muted}>Could not load the text funnel.</p>
      </div>
    );
  const k = view.kpis;
  const p = view.previous;
  if (k.confirmedVisits === 0 && p.confirmedVisits === 0) {
    return (
      <div className={a.stack}>
        {toolbar}
        <p className={a.muted}>
          No visits confirmed in this range. The funnel starts when the front desk confirms visits
          on the Today tab.
        </p>
      </div>
    );
  }
  const active = selected ?? view.biggestDropStep ?? view.steps[0]?.key ?? 'confirmed';

  return (
    <div className={a.stack}>
      {toolbar}
      <div className={a.kpis}>
        <Kpi label="Visits confirmed" value={k.confirmedVisits} sub={`${k.texted} texted`} />
        <Kpi label="Delivered" value={rateText(k.deliveredRate)} sub={`${k.failed} failed`} />
        <Kpi
          label="Replied"
          value={rateText(k.replyRate)}
          sub={
            <>
              <Delta current={k.replyRate} previous={p.replyRate} /> of delivered
            </>
          }
        />
        <Kpi
          label="Live on Google"
          value={k.matched}
          accent
          sub={`${rateText(k.visitToReviewRate)} of visits · ${k.postConfirmed} confirmed by patient`}
        />
        <Kpi
          label="Private feedback"
          value={k.privateFeedback}
          sub={`${k.optedOut} opted out · ${k.staffNeeded} needed staff`}
        />
        <Kpi
          label="Cost per review"
          value={view.cost?.perReviewUsd != null ? usd(view.cost.perReviewUsd) : '—'}
          sub="texts + AI, estimated"
        />
      </div>
      <AnalyticsCard
        title="Every confirmed visit, from the front desk to Google"
        sub={`Median visit end → text ${formatMinutes(k.medianMinutesVisitToText)} · median text → reply ${formatMinutes(k.medianMinutesTextToReply)}`}
      >
        <div className={a.split}>
          <TextFunnel view={view} selected={active} onSelect={setSelected} />
          <StepDetail view={view} stepKey={active} />
        </div>
      </AnalyticsCard>
      <div className={a.grid3}>
        <RatingMixCard view={view} />
        <DeliveryCard view={view} />
        <CostCard view={view} />
      </div>
      <div className={a.grid2}>
        <ReplyByHourCard view={view} />
        <ByDelayCard view={view} />
      </div>
      <div className={a.grid2}>
        <StaffCard view={view} />
        <ByProviderCard view={view} />
      </div>
      <RecentConversationsCard view={view} />
    </div>
  );
}
